import { debounce, DebouncedFunc } from 'lodash-es'
import { CustomizationOptions, View } from '@ally/data-types-customization'

import { CustomizeDispatch } from './CustomizeDashboardReducer'
import { CustomizeDataObj, DashboardView, ViewsObj } from '../types'
import { SaveDataFunc } from './CustomizeDashboardContext'

export const DEBOUNCE_TIME = 3000

export function filterDashboardView(view: View | ViewsObj): boolean {
  return view.name !== 'DASHBOARD'
}

let debouncedPatch: DebouncedFunc<() => void> | null = null
let payloadForDebouncedPatch: DashboardView | null

function reset(): void {
  debouncedPatch = null
  payloadForDebouncedPatch = null
}

export function saveDashboardView(
  dispatch: CustomizeDispatch,
  payload: DashboardView,
  customizationsApiCall: SaveDataFunc,
  existingViews: View[] | ViewsObj[],
  method: 'POST' | 'PATCH',
  defaultCustomizationPayload: CustomizeDataObj | Partial<CustomizeDataObj>,
): void {
  dispatch({ type: 'RESET CustomizeDashboard API alert' })
  dispatch({ type: 'set dashboardView', payload })

  // if the user changes the view we want to cancel the previous function call and start a new one
  if (debouncedPatch && payload !== payloadForDebouncedPatch) {
    debouncedPatch.cancel()
    reset()
    // TODO enhancement - if the user switches to a new view and back to the view they had we should not make a PATCH call since nothing really changed
  }

  if (!debouncedPatch) {
    payloadForDebouncedPatch = payload
    debouncedPatch = debounce(async () => {
      const payloadData = {
        channel: 'WEB',
        // if first time customizing, we need to add all fields for POST
        ...(method === 'POST' ? defaultCustomizationPayload : {}),
        views: [
          ...existingViews.filter(filterDashboardView),
          {
            name: 'DASHBOARD',
            value: payload,
          },
        ],
      }

      try {
        const response = await customizationsApiCall({
          payload: payloadData,
        })

        // this will only exist if a POST request was made
        const customizationId =
          (response?.payload as CustomizationOptions)?.customizationId ?? null

        if (method === 'POST') {
          dispatch({
            type: 'set customizeApiData',
            payload: {
              ...payloadData,
              ...(customizationId ? { customizationId } : {}),
            },
          })
        }
      } catch (error) {
        dispatch({
          type: 'CustomizeDashboard API alert',
          payload: {
            shouldRender: true,
            type: 'error',
            isDashboardError: true,
          },
        })
      }
      reset()
    }, DEBOUNCE_TIME)
  }
  debouncedPatch()
}

export async function saveCustomizationsUtil(
  dispatch: CustomizeDispatch,
  payload: CustomizeDataObj | Partial<CustomizeDataObj>,
  saveApiCall: SaveDataFunc,
  isDefaultWidgetsOnActive: boolean,
): Promise<void> {
  dispatch({ type: 'RESET CustomizeDashboard API alert' })

  try {
    dispatch({ type: 'set isSaving', payload: true })
    const response = await saveApiCall({
      payload: {
        ...payload,
        channel: 'WEB',
      },
    })

    dispatch({ type: 'set isSaving', payload: false })

    // this will only exist if a POST request was made
    const customizationId =
      (response?.payload as CustomizationOptions)?.customizationId ?? null

    // sync UI with new customization data the user saved
    // if neccessary add the new customizationId if it's the user's first time saving
    dispatch({
      type: 'set customizeApiData',
      payload: {
        ...payload,
        ...(customizationId ? { customizationId } : {}),
      },
    })

    dispatch({
      type: 'CustomizeDashboard API alert',
      payload: {
        shouldRender: true,
        type: 'success',
        isDashboardError: false,
      },
    })
  } catch (error) {
    dispatch({ type: 'set isSaving', payload: false })
    dispatch({
      type: 'CustomizeDashboard API alert',
      payload: {
        shouldRender: true,
        type: 'error',
        isDashboardError: false,
      },
    })
  }
}
